import { actionMeta, hasKnownTime, hasMeaningfulQuantity, isEvidenceGap } from './logbook';
import type { LogbookEvent } from '../types/logbook';

/**
 * How the evidence list is presented, as opposed to which evidence was asked for.
 *
 * These are display decisions, deliberately separate from the filters that become a Loglibrarian
 * query. The API combines filters with AND on exact values and has no "not equal", and an action
 * allowlist caps at 32 while the supported set runs past a hundred, so it would silently drop both
 * the overflow and any action a new mod adapter starts emitting. Excluding noise upstream is therefore not available without making the panel lie
 * about coverage, so it is done here, on the page, where the hidden count can stay on screen.
 *
 * Two classes of record are never hidden, whatever the settings say. A capture gap is the one record
 * that means evidence is missing rather than absent, and it carries `actor_kind: "unknown"`, so an
 * unattributed filter would bury exactly the thing the panel exists to surface. A record already held
 * for a case was kept on purpose by a moderator, so a view setting must not take it off screen.
 */

export type LogbookNoiseClass = 'state' | 'unattributed' | 'automation' | 'carryon_attempted' | 'generic_menu';

export const LOGBOOK_NOISE_CLASSES: { id: LogbookNoiseClass; label: string; description: string }[] = [
  {
    id: 'state',
    label: 'Position samples',
    description: 'player.state telemetry. High volume, and a position is an observation rather than a movement replay.',
  },
  {
    id: 'unattributed',
    label: 'Unattributed',
    description: 'No initiator reached the capture, so no actor is named. Common for storage networks, FTB Teams and claim notifications.',
  },
  {
    id: 'automation',
    label: 'Automation',
    description: 'An automated system rather than a person. Its owner is not its actor.',
  },
  {
    id: 'carryon_attempted',
    label: 'Unconfirmed Carry On',
    description: 'Carry On records that only reached attempted, so no block or entity change was confirmed alongside them.',
  },
  {
    id: 'generic_menu',
    label: 'Generic menu opens',
    description: 'menu.open on an unnamed vanilla container type such as minecraft:generic_9x3. The menu type says nothing about what was actually opened, and access is not a transfer.',
  },
];

export interface LogbookViewOptions {
  /** Noise classes currently shown. Everything not listed here is hidden. */
  shown: LogbookNoiseClass[];
  /**
   * Largest gap between adjacent records that still counts as one run, in milliseconds.
   * Zero turns grouping off.
   */
  groupWindowMs: number;
}

export const LOGBOOK_GROUP_WINDOWS: { ms: number; label: string }[] = [
  { ms: 0, label: 'No grouping' },
  { ms: 60_000, label: 'Group within 1 minute' },
  { ms: 300_000, label: 'Group within 5 minutes' },
  { ms: 900_000, label: 'Group within 15 minutes' },
  { ms: 3_600_000, label: 'Group within 1 hour' },
];

/**
 * Position telemetry, unattributed records, automations, unconfirmed Carry On handling and generic
 * menu opens are all noise until someone asks for them.
 */
export const DEFAULT_LOGBOOK_VIEW: LogbookViewOptions = {
  shown: [],
  groupWindowMs: 300_000,
};

/** A record no view setting may hide. */
function alwaysVisible(event: LogbookEvent, heldEventIds?: Set<string>): boolean {
  return isEvidenceGap(event) || (heldEventIds?.has(event.event_id) ?? false);
}

/**
 * Every noise class a record belongs to, not just the first.
 *
 * A record can qualify twice (an automation whose Carry On handling never confirmed, say), and it is
 * hidden when any of its classes is hidden. Classifying on first match instead would let showing one
 * class drag in records the other class was meant to keep out.
 */
function noiseClassesOf(event: LogbookEvent): LogbookNoiseClass[] {
  const classes: LogbookNoiseClass[] = [];
  if (event.action === 'player.state') classes.push('state');
  // A generic menu type is the vanilla fallback shape (generic_9x3 and friends). It names a size,
  // not a container, so on its own it is the least informative record Logbook emits.
  if (event.action === 'menu.open' && /^minecraft:generic_/.test(event.object_id)) classes.push('generic_menu');
  if (event.action.startsWith('carryon.') && event.outcome === 'attempted') classes.push('carryon_attempted');
  if (event.actor_kind === 'automation') classes.push('automation');
  if (event.actor_kind === 'unknown') classes.push('unattributed');
  return classes;
}

export interface LogbookPartition {
  visible: LogbookEvent[];
  /** How many were hidden, per class, so the total is never silently smaller. */
  hidden: Record<LogbookNoiseClass, number>;
  hiddenTotal: number;
}

const EMPTY_TALLY: Record<LogbookNoiseClass, number> = { state: 0, unattributed: 0, automation: 0, carryon_attempted: 0, generic_menu: 0 };

/** Splits a page into what is shown and a per-class count of what was held back. */
export function partitionEvents(
  events: LogbookEvent[],
  options: LogbookViewOptions,
  heldEventIds?: Set<string>,
): LogbookPartition {
  const hidden: Record<LogbookNoiseClass, number> = { ...EMPTY_TALLY };
  const visible: LogbookEvent[] = [];
  let hiddenTotal = 0;
  for (const event of events) {
    const classes = noiseClassesOf(event);
    const hiding = classes.filter((noise) => !options.shown.includes(noise));
    if (hiding.length === 0 || alwaysVisible(event, heldEventIds)) {
      visible.push(event);
      continue;
    }
    // Counted once, against the first class that hid it, so the tallies still sum to the total.
    hidden[hiding[0]] += 1;
    hiddenTotal += 1;
  }
  return { visible, hidden, hiddenTotal };
}

// ── Grouping ────────────────────────────────────────────────────────────────

export interface LogbookRun {
  /** Stable across renders for the same run. */
  id: string;
  events: LogbookEvent[];
  /** True once the run holds more than one record. */
  grouped: boolean;
  /** Newest timestamp in the run, or 0 when the time is unknown. */
  latestAt: number;
  /** Oldest timestamp in the run. */
  earliestAt: number;
  /** Summed quantity, where the action's quantity means anything. */
  totalQuantity: number | null;
  /** Distinct object ids in the run, capped for display. */
  objectIds: string[];
  /** Distinct rounded positions, so "one spot" reads differently from "a spree". */
  positionCount: number;
}

/**
 * What makes two adjacent records the same run.
 *
 * Server, outcome and dimension are part of the identity, not just actor and action: folding a
 * `denied` into a run of `completed`, or two servers into one summary line, would hide the very
 * distinction a moderator is reading for.
 */
function runKey(event: LogbookEvent): string {
  return [event.actor_id, event.actor_kind, event.action, event.server_id, event.outcome, event.dimension].join(' ');
}

function position(event: LogbookEvent): string {
  return `${Math.round(event.x)},${Math.round(event.y)},${Math.round(event.z)}`;
}

function summarise(events: LogbookEvent[]): LogbookRun {
  const first = events[0];
  const timestamps = events.filter(hasKnownTime).map((event) => event.timestamp);
  const objects = new Set<string>();
  const positions = new Set<string>();
  let quantity = 0;
  let quantityMeans = false;
  for (const event of events) {
    if (event.object_id) objects.add(event.object_id);
    positions.add(position(event));
    if (hasMeaningfulQuantity(event)) {
      quantity += event.quantity;
      quantityMeans = true;
    }
  }
  return {
    // The first event id is unique and stable, so it identifies the run without a counter.
    id: first.event_id,
    events,
    grouped: events.length > 1,
    latestAt: timestamps.length > 0 ? Math.max(...timestamps) : 0,
    earliestAt: timestamps.length > 0 ? Math.min(...timestamps) : 0,
    totalQuantity: quantityMeans ? quantity : null,
    objectIds: Array.from(objects).slice(0, 8),
    positionCount: positions.size,
  };
}

/**
 * Collapses consecutive runs of the same actor doing the same thing.
 *
 * Only adjacent records are folded together, so the list stays in the order the service returned it.
 * Grouping records that were not next to each other would reorder evidence, which is not a
 * presentation choice to make silently on a page someone builds a case from.
 *
 * A record whose time is unknown (timestamp 0 labels legacy evidence) never joins a run, because
 * there is no gap to measure against it.
 */
export function groupEvents(events: LogbookEvent[], windowMs: number): LogbookRun[] {
  if (events.length === 0) return [];
  if (windowMs <= 0) return events.map((event) => summarise([event]));

  const runs: LogbookRun[] = [];
  let current: LogbookEvent[] = [];
  let currentKey = '';

  const flush = () => {
    if (current.length > 0) runs.push(summarise(current));
    current = [];
  };

  for (const event of events) {
    const key = runKey(event);
    const previous = current[current.length - 1];
    const joins = previous !== undefined
      && key === currentKey
      && hasKnownTime(event)
      && hasKnownTime(previous)
      && Math.abs(previous.timestamp - event.timestamp) <= windowMs;
    if (joins) {
      current.push(event);
      continue;
    }
    flush();
    current = [event];
    currentKey = hasKnownTime(event) ? key : '';
  }
  flush();
  return runs;
}

/** One-line summary of what a grouped run did, used as the row's sentence. */
export function describeRun(run: LogbookRun): string {
  const meta = actionMeta(run.events[0].action);
  const times = `${run.events.length} times`;
  return run.totalQuantity !== null && run.totalQuantity > 0
    ? `${meta.verb} ${run.totalQuantity.toLocaleString()} across ${times}`
    : `${meta.verb} ${times}`;
}

// ── URL round trip ──────────────────────────────────────────────────────────

const NOISE_IDS = LOGBOOK_NOISE_CLASSES.map((entry) => entry.id);

/** Only deviations from the default are written, so an ordinary search keeps a clean link. */
export function viewToParams(view: LogbookViewOptions, params: URLSearchParams): void {
  if (view.shown.length > 0) params.set('show', view.shown.join(','));
  if (view.groupWindowMs !== DEFAULT_LOGBOOK_VIEW.groupWindowMs) params.set('group', String(view.groupWindowMs));
}

export function viewFromParams(params: URLSearchParams): LogbookViewOptions {
  const raw = params.get('show');
  const shown = raw
    ? raw.split(',').map((part) => part.trim()).filter((part): part is LogbookNoiseClass => (NOISE_IDS as string[]).includes(part))
    : [];
  const group = Number(params.get('group'));
  const groupWindowMs = LOGBOOK_GROUP_WINDOWS.some((entry) => entry.ms === group)
    ? group
    : DEFAULT_LOGBOOK_VIEW.groupWindowMs;
  return { shown: Array.from(new Set(shown)), groupWindowMs };
}

export function toggleNoiseClass(view: LogbookViewOptions, noise: LogbookNoiseClass): LogbookViewOptions {
  return {
    ...view,
    shown: view.shown.includes(noise) ? view.shown.filter((entry) => entry !== noise) : [...view.shown, noise],
  };
}
